import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { ArchesRules, ArchesState } from '../vertebras-types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';

/**
 * The arches in the wind (docs/sendas/barbaro.md, chapter 2, act 1: «Los arcos»; the ground in battle/vertebras-layout.ts).
 * A way of wet arches over the sea, a head every few hexes and narrow spans between, a mooring post on each head. The
 * wind comes in gusts from the side, seen `warn` before in the foam lifting in a line: whatever it catches in the open is
 * pushed toward the drop, and past the way's edge a wolf is gone and the hero falls (back on the last post it passed,
 * hurt). By a post the gust doesn't move the hero: hold to one and let it sweep the pack off the arch (the rule of El
 * Cuerno 2's tilting ground, as wind). Bera and Terrón come behind along the way (they know the wind); at one gap Terrón
 * balks and the hero has to go back and push him over («Es piedra, no se rompe»). Won at the last firm arch, Terrón
 * over the gap.
 *
 * Cost: a gust looks at the hero and the pack once; Bera and Terrón walk the way's points (no path finding).
 */

const fresh = (r: ArchesRules): ArchesState => ({
  gust: 0,
  last: 0,
  far: 0,
  next: r.pack.first,
  behind: r.pack.behindEvery,
  crow: r.crows.first,
  balk: 0,
  held: 0,
  terronAt: 0,
  beraAt: 0,
  falls: 0,
  overs: 0,
  marked: false,
  done: -1,
});

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/** The way's point nearest a spot, looked for round a guess (the way is long: a window of it). */
export const archesAt = (r: ArchesRules, x: number, y: number, guess: number): number => {
  let best = guess;
  let bd = 0x7fffffff;
  const lo = imax(0, guess - 14);
  const hi = imin(r.path.length - 1, guess + 14);
  for (let i = lo; i <= hi; i++) {
    const p = r.path[i] as Pt;
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  return best;
};

/** Within reach of a mooring post (the gust doesn't move it). */
export const archesHeld = (r: ArchesRules, x: number, y: number): boolean => r.posts.some((p) => withinRange(p.x, p.y, x, y, r.post));

/** The next gust's warning is out (it strikes within `warn`); ticks to it (−1: none left). */
export const archesWarn = (s: BattleState, r: ArchesRules, w: ArchesState): boolean => {
  const g = r.plan[w.gust];
  return !!g && s.waveT >= g.t - r.warn;
};
export const archesNext = (s: BattleState, r: ArchesRules, w: ArchesState): number => {
  const g = r.plan[w.gust];
  return g ? imax(0, g.t - s.waveT) : -1;
};

/** The tap on Terrón's mark: the hero goes to him (to push him over the gap). */
export const archesOrder = (s: BattleState, h: Hero, at: number): void => {
  const t = npc(s, 'terron');
  if (at !== 0 || !t) return;
  orderMove(s, h, t.x, t.y + 400);
  h.target = 0;
  h.forced = false;
};

/** Pushed `d` along x by the wind while the way holds; how much of it was left at the edge (over it, if not 0). */
const shove = (s: BattleState, o: { x: number; y: number }, dir: number, d: number): number => {
  let left = d;
  while (left > 0) {
    const step = imin(100, left);
    if (!heroWalkable(s, cellAtWorld(s, o.x + dir * step, o.y))) return left;
    o.x += dir * step;
    left -= step;
  }
  return 0;
};

const gust = (s: BattleState, r: ArchesRules, w: ArchesState, dir: number, cue: (c: string) => void): void => {
  ev(s, { t: 'senda', kind: 'vGust', npc: '', x: 0, y: 0, n: dir });
  const h = s.heroes.find((x) => x.downTicks === 0);
  for (const e of s.enemies) {
    if (e.hp <= 0 || NIGHTMARES[e.kind].flying) continue;
    if (shove(s, e, dir, r.push) < 300) continue;
    // Over the edge: into the sea below (the hero's, as a kill).
    ev(s, { t: 'senda', kind: 'vOver', npc: '', x: e.x + dir * 700, y: e.y, n: dir });
    damageEnemy(s, e, e.hp + e.maxHp, { pid: h?.pid ?? '', ignoresArmor: true });
    w.overs++;
    cue('over1');
  }
  if (!h || archesHeld(r, h.x, h.y)) return;
  const left = shove(s, h, dir, r.push);
  h.ax = h.x;
  h.ay = h.y;
  if (left < 300) {
    if (h.moving) orderMove(s, h, h.destX, h.destY);
    return;
  }
  // Off the arch: the sea and the rocks, then hauled back up by the last post it passed.
  ev(s, { t: 'senda', kind: 'vFall', npc: '', x: h.x + dir * 900, y: h.y, n: dir });
  damageHero(s, h, mulBp(h.stats.maxHp, r.fallBp), null, 'physical', true);
  const back = r.heads[w.last] as Pt;
  h.x = h.destX = h.ax = back.x;
  h.y = h.destY = h.ay = back.y;
  h.path = [];
  h.moving = false;
  h.stun = imax(h.stun, r.stun);
  w.falls++;
  cue('fall1');
};

/** A person along the way: on toward its point of it, a point at a time (never past `stop`). */
const walkWay = (r: ArchesRules, n: SendaNpc, at: number, to: number, stop: number): number => {
  const goal = imin(to, stop);
  if (at === goal) return at;
  const k = at < goal ? at + 1 : at - 1;
  const p = r.path[k] as Pt;
  n.moving = true;
  return stepToward(n, p.x, p.y, r.pace) ? k : at;
};

/** The pack out along the way: `n` wolves round its point `i`. */
const band = (s: BattleState, r: ArchesRules, i: number, n: number): void => {
  for (let k = 0; k < n; k++) {
    const p = r.path[imax(0, imin(r.path.length - 1, i + k))] as Pt;
    spawnEnemy(s, 'wolf', 1, false, 0, p.x + (k % 2) * 200 - 100, p.y, r.wolfHp);
  }
};

export const archesTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.vert?.arches;
  const st = s.senda;
  if (!r || !st) return;
  const vs = (st.vert ??= {});
  const w = (vs.arches ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const at = h ? archesAt(r, h.x, h.y, w.far) : w.far;
  if (h) {
    w.far = imax(w.far, at);
    // The last head passed (where a fall brings it back), Sigrun's mark seen.
    for (let k = w.last + 1; k < r.heads.length; k++) {
      const c = r.heads[k] as Pt;
      if (withinRange(c.x, c.y, h.x, h.y, 1300)) w.last = k;
    }
    const m = r.posts[r.mark] as Pt;
    if (!w.marked && withinRange(m.x, m.y, h.x, h.y, 1800)) {
      w.marked = true;
      ev(s, { t: 'senda', kind: 'vMark', npc: '', x: m.x, y: m.y });
      cue('mark');
    }
  }
  // The gusts: the foam lifting `warn` before, then the wind.
  const g = r.plan[w.gust];
  if (g) {
    if (s.waveT === g.t - r.warn) {
      ev(s, { t: 'senda', kind: 'vGustWarn', npc: '', x: h?.x ?? 0, y: h?.y ?? 0, n: g.dir });
      if (w.gust === 0) cue('warn1');
    }
    if (s.waveT >= g.t) {
      gust(s, r, w, g.dir, cue);
      w.gust++;
    }
  }
  // Bera and Terrón behind along the way; Terrón stops at the gap until pushed.
  const t = npc(s, 'terron');
  const b = npc(s, 'bera');
  const stop = w.balk === 2 ? r.path.length - 1 : r.balk;
  if (t) {
    t.moving = false;
    w.terronAt = walkWay(r, t, w.terronAt, imax(0, at - 2), stop);
    if (w.balk === 0 && w.terronAt === r.balk && at > r.balk + 3) {
      w.balk = 1;
      ev(s, { t: 'senda', kind: 'vBalk', npc: 'terron', x: t.x, y: t.y });
      cue('balk');
    }
    if (w.balk === 1) {
      if (h && !h.moving && withinRange(h.x, h.y, t.x, t.y, r.nudgeReach)) {
        if (++w.held >= r.nudge) {
          w.balk = 2;
          w.held = 0;
          w.terronAt = r.over;
          const p = r.path[r.over] as Pt;
          ev(s, { t: 'senda', kind: 'vNudge', npc: 'terron', x: p.x, y: p.y });
          t.x = p.x;
          t.y = p.y;
          cue('nudged');
        }
      } else w.held = 0;
    }
  }
  if (b) {
    b.moving = false;
    // Bera stays by Terrón while he balks.
    w.beraAt = walkWay(r, b, w.beraAt, imax(0, (w.balk === 1 ? w.terronAt : at) - 4), r.path.length - 1);
  }
  // The pack: from ahead along the way, now and then from behind; crows off the sea.
  if (h && s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.pack.every[0], r.pack.every[1]);
    band(s, r, imin(r.path.length - 3, at + 16), rngRange(s.rng, r.pack.band[0], r.pack.band[1]));
  }
  if (h && s.waveT >= w.behind) {
    w.behind = s.waveT + r.pack.behindEvery;
    if (at > 14) band(s, r, at - 14, r.pack.behind);
  }
  if (h && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    for (let n = 0; n < r.crows.n; n++) spawnEnemy(s, 'crow', 1, false, 0, h.x + 7500 + n * 400, h.y - 1500 + n * 500, imax(1, r.wolfHp >> 1));
  }
  // The last firm arch, Terrón over the gap.
  if (h && w.balk === 2 && withinRange(h.x, h.y, r.end.x, r.end.y, r.endReach)) w.done = s.tick;
};

/** The way walked, of all of it. */
export const archesGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.vert?.arches;
  const w = s.senda?.vert?.arches;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.far, need: imax(1, r.path.length - 1) };
};

export const archesResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.vert?.arches;
  return w && w.done >= 0 && s.tick >= w.done + 12 ? 'won' : null;
};

/** (The pack walks as any pack does: nothing of its own.) */
export const archesHold = (_s: BattleState, _e: Enemy): boolean => false;
